import React from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import useAiChat from '../Model/useAiChat';
import AiChattingView from './AiChattingView';
import LeftArrowLight from '../../image/LeftArrow-light.png';
import LeftArrowDark from '../../image/LeftArrow-dark.png';
import AiIconLight from '../../image/ClickAi-light.png';
import AiIconDark from '../../image/ClickAi-dark.png';
import TokenLightIcon from '../../image/Token-light.png';
import TokenDarkIcon from '../../image/Token-dark.png';
import NewChatLight from '../../image/NewChat-light.png';
import NewChatDark from '../../image/NewChat-dark.png';
import DeleteIcon from '../../image/Delete(stroke).svg';
import DeleteHoverIcon from '../../image/Delete(stroke)_hover.svg';

export default function AiView() {
  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    sessions,
    sessionId,
    chatMessagesContainerRef,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,
    __testRunAgent,
    __testGetClient,
  } = useAiChat();

  // 테스트 러너 등록 (dev 환경에서만, ref 사용으로 1회만 실행) //
  React.useEffect(() => {
    if (__testRunAgent && typeof window !== 'undefined') {
      import('../Test/testCases').then(({ testCasesL1toL4, testCasesL5toL8, testCasesL9toL11, testCasesContextAware }) => {
        const allCases = [...(testCasesL1toL4 || []), ...(testCasesL5toL8 || []), ...(testCasesL9toL11 || []), ...(testCasesContextAware || [])];
        window.__aiTestCases = allCases;

        import('../Test/testRunner').then(({ collectResults }) => {
          const runFn = (input) => __testRunAgent.current(input);
          const client = __testGetClient();
          window.__collectAiTests = (options = {}) => collectResults(allCases, runFn, { ...options, resetFn: handleNewChat });
          window.__collectByLayer = (layer) => collectResults(allCases, runFn, { layer, resetFn: handleNewChat });
          window.__collectById = (id) => collectResults(allCases, runFn, { id, resetFn: handleNewChat });
          window.__openaiClient = client;
          console.log('[TestRunner] 등록 완료');
        });
      });
    }
  }, []);

  // 뒤로가기 //
  function onClickGoBack() {
    history.goBack();
  }

  return (
    <TopLevelWrapper backgroundColor={theme.background}>
      <ComponentsWrapper>
        <NavigationBarWrapper>
          <NavigationBarLeftWrapper>
            <NavigationView $bg={theme.primary}>
              <GoBackButton onClick={onClickGoBack}>
                <Image width={11} height={16} src={isDark ? LeftArrowDark : LeftArrowLight} />
              </GoBackButton>
              <AiComponent>
                <Image width={22} height={24} src={isDark ? AiIconDark : AiIconLight} />
                <Text fontFamily='NotoSansKR-Medium' fontSize={18} color={theme.white} cursor='default'>AI</Text>
              </AiComponent>
            </NavigationView>
            <ExplainWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>링코 AI Manager</Text>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>AI 분석을 통해 운영 데이터를 최적화하고 운영 효율을 극대화하세요</Text>
            </ExplainWrapper>
          </NavigationBarLeftWrapper>
          <TokenBox $borderColor={theme.primary}>
            <Image width={22} height={18} src={isDark ? TokenDarkIcon : TokenLightIcon} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.primary} cursor='default'>1,222,333</Text>
          </TokenBox>
        </NavigationBarWrapper>
        <InfoWrapper>
          <ChattingSideBarWrapper>
            <NewChattingButton onClick={handleNewChat}>
              <Image width={20} height={19} src={isDark ? NewChatDark : NewChatLight} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={isDark ? theme.primary : theme.text} cursor='pointer'>새 채팅</Text>
            </NewChattingButton>
            <NewChattingListTextWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={isDark ? theme.primary : theme.text} cursor='default'>내 채팅 목록</Text>
            </NewChattingListTextWrapper>
            <SessionList $thumbColor={theme.gray}>
              {sessions.map(session => (
                <SessionItem key={session.sessionId} $isActive={session.sessionId === sessionId} $activeColor={theme.primaryAlpha20} onClick={() => loadConversations(session.sessionId)}>
                  <SessionTitle color={theme.text}>{session.sessionTitle}</SessionTitle>
                  <DeleteButton onClick={(e) => { e.stopPropagation(); deleteSession(session.sessionId); }}>
                    <DeleteImg src={DeleteIcon} />
                    <DeleteHoverImg src={DeleteHoverIcon} />
                  </DeleteButton>
                </SessionItem>
              ))}
            </SessionList>
          </ChattingSideBarWrapper>
          <AiChattingView
            chatMessages={chatMessages}
            chatInput={chatInput}
            setChatInput={setChatInput}
            handleSend={handleSend}
            handleStop={handleStop}
            isResponding={isResponding}
            chatMessagesContainerRef={chatMessagesContainerRef}
            onSmsSend={handleSmsSend}
            onIndividualSmsSend={handleIndividualSmsSend}
            onIndividualSmsGenerate={handleIndividualSmsGenerate}
          />
        </InfoWrapper>
      </ComponentsWrapper>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  
  width: 100%;

  justify-content: flex-start;
  align-items: flex-start;

  background-color: ${props => props.backgroundColor};
`;

const ComponentsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  min-height: 590px;
  height: calc(100vh - 102px);
`;

const NavigationBarWrapper = styled.div`
  display: flex;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 60px;
`;

const NavigationBarLeftWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;
`;

const NavigationView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 4px 20px 4px 8px;

  height: 40px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.$bg};
`;

const GoBackButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  cursor: pointer;
`;

const AiComponent = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  gap: 13px;
`;

const ExplainWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: baseline;

  padding: 0 0 0 20px;

  gap: 10px;
`;

const TokenBox = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  padding: 0 15px 0 15px;

  height: 40px;

  gap: 10px;

  border-radius: 10px;
  border: 1px solid ${props => props.$borderColor};
`;

const InfoWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px 0 0 0;

  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  gap: 15px;
`;

const ChattingSideBarWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 166px;
  height: 100%;
  min-height: 0;

  box-sizing: border-box;
`;

const NewChattingButton = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 0 10px;

  width: 100%;
  height: 40px;

  gap: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const NewChattingListTextWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  padding: 0 10px 0 10px;

  width: 100%;
  height: 40px;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  flex: 1;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.$thumbColor};
    border-radius: 2px;
  }
`;

const SessionItem = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 8px 10px;

  border-radius: 6px;

  cursor: pointer;

  background-color: ${props => props.$isActive ? props.$activeColor : 'transparent'};

  &:hover {
    background-color: ${props => props.$activeColor};
  }

  &:hover > div {
    opacity: 1;
  }
`;

const DeleteButton = styled.div`
  position: relative;

  display: flex;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  flex-shrink: 0;

  opacity: 0;

  transition: opacity 0.15s ease-in-out;

  &:hover > img:first-child {
    opacity: 0;
  }

  &:hover > img:last-child {
    opacity: 1;
  }
`;

const DeleteImg = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  object-fit: contain;

  opacity: 1;

  transition: opacity 0.15s ease-in-out;
`;

const DeleteHoverImg = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  object-fit: contain;

  opacity: 0;

  transition: opacity 0.15s ease-in-out;
`;

const SessionTitle = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 13px;

  color: ${props => props.color};

  margin: 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Image = styled.img`
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  object-fit: contain;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  cursor: ${props => props.cursor};
`;
